import { useMemo, useCallback } from 'react';

import { collection, collectionGroup, Query, query, where } from 'firebase/firestore';

import { useCollectionData } from 'react-firebase-hooks/firestore';

import { ProfileKey } from '../../shared/types/ProfileKey';
import { useAuth } from '../contextProviders/AuthProvider';
import { firestore } from '../firebase';
import Membership, { MembershipStatus, MEMBERSHIP_STATUSES } from '../types/Membership';

import { useFirestoreError } from './useFirestoreError';

interface UseMembershipsOfPersonProps {
  profileKeys?: ProfileKey | null;
  personId?: string | null;
  orgId?: string | null;
  status?: MembershipStatus;
  includeAllStatuses?: boolean; // When true, ignores status and gets all memberships
}

export default function useMembershipsOfPerson(props: UseMembershipsOfPersonProps) {
  const { profileKeys, personId, orgId, status = MEMBERSHIP_STATUSES.ACTIVE, includeAllStatuses = false } = props;
  const { isGod } = useAuth();

  const getAllOrgIds = useCallback(
    (): string[] => {
      if (!profileKeys) return [];
      return Array.from(
        new Set([
          ...(profileKeys.orgIds || []),
          ...(profileKeys.adminOrgIds || []),
          ...(profileKeys.pendingOrgIds || []),
          ...(profileKeys.pendingAdminOrgIds || []),
          ...(profileKeys.inactiveOrgIds || []),
          ...(profileKeys.inactiveAdminOrgIds || []),
        ])
      );
    },
    [profileKeys]
  );

  const getOrgIdsByStatus = useCallback(
    (status: MembershipStatus): string[] => {
      if (!profileKeys) return [];

      switch (status) {
        case MEMBERSHIP_STATUSES.ACTIVE:
        case MEMBERSHIP_STATUSES.DEPRECATED_ACTIVE:
          return Array.from(
            new Set([
              ...(profileKeys.orgIds || []),
              ...(profileKeys.adminOrgIds || []),
            ])
          );

        case MEMBERSHIP_STATUSES.PENDING:
          return Array.from(
            new Set([
              ...(profileKeys.pendingOrgIds || []),
              ...(profileKeys.pendingAdminOrgIds || []),
            ])
          );

        case MEMBERSHIP_STATUSES.INACTIVE:
          return Array.from(
            new Set([
              ...(profileKeys.inactiveOrgIds || []),
              ...(profileKeys.inactiveAdminOrgIds || []),
            ])
          );

        default:
          return [];
      }
    },
    [profileKeys]
  );

  // Get both new and deprecated status values for backwards-compatible queries
  const getStatusValues = useCallback((status: MembershipStatus): MembershipStatus[] => {
    switch (status) {
      case MEMBERSHIP_STATUSES.ACTIVE:
      case MEMBERSHIP_STATUSES.DEPRECATED_ACTIVE:
        return [MEMBERSHIP_STATUSES.ACTIVE, MEMBERSHIP_STATUSES.DEPRECATED_ACTIVE];
      case MEMBERSHIP_STATUSES.PENDING:
        return [MEMBERSHIP_STATUSES.PENDING];
      case MEMBERSHIP_STATUSES.INACTIVE:
        return [MEMBERSHIP_STATUSES.INACTIVE];
      case MEMBERSHIP_STATUSES.DELETED:
        return [MEMBERSHIP_STATUSES.DELETED];
      default:
        return [status];
    }
  }, []);

  const membershipQuery = useMemo(() => {
    if (!personId) return null;
    if (!profileKeys) return null;

    const validOrgIds = includeAllStatuses ? getAllOrgIds() : getOrgIdsByStatus(status);

    if (validOrgIds.length === 0) {
      return null;
    }

    if (orgId) {
      if (isGod || validOrgIds.includes(orgId)) {
        if (includeAllStatuses) {
          // When includeAllStatuses is true, don't filter by status
          return query(
            collection(firestore, 'org', orgId, 'membership') as Query<Membership>,
            where('itemId', '==', personId)
          );
        } else {
          return query(
            collection(firestore, 'org', orgId, 'membership') as Query<Membership>,
            where('itemId', '==', personId),
            where('status', 'in', getStatusValues(status))
          );
        }
      } else {
        return null;
      }
    }

    // If god or querying own memberships, can see all memberships
    if (isGod || personId === profileKeys.itemId) {
      if (includeAllStatuses) {
        // When includeAllStatuses is true, don't filter by status
        return query(
          collectionGroup(firestore, 'membership') as Query<Membership>,
          where('itemId', '==', personId),
          where('orgId', 'in', validOrgIds)
        );
      } else {
        return query(
          collectionGroup(firestore, 'membership') as Query<Membership>,
          where('itemId', '==', personId),
          where('orgId', 'in', validOrgIds),
          where('status', 'in', getStatusValues(status))
        );
      }
    }

    // Otherwise, can only see active memberships in shared orgs
    if (includeAllStatuses) {
      // When includeAllStatuses is true, don't filter by status
      return query(
        collectionGroup(firestore, 'membership') as Query<Membership>,
        where('itemId', '==', personId),
        where('orgId', 'in', validOrgIds)
      );
    } else {
      return query(
        collectionGroup(firestore, 'membership') as Query<Membership>,
        where('itemId', '==', personId),
        where('orgId', 'in', validOrgIds),
        where('status', 'in', getStatusValues(status))
      );
    }
  }, [personId, profileKeys, isGod, orgId, getAllOrgIds, getOrgIdsByStatus, getStatusValues, status, includeAllStatuses]);

  const [memberships, isMembershipsLoading, membershipsError] = useCollectionData<Membership>(membershipQuery);
  useFirestoreError('useMembershipsOfPerson', membershipsError);

  const memoizedReturn = useMemo(() => {
    if (!isMembershipsLoading && !membershipsError && profileKeys === null) {
      return {
        memberships: [],
        isMembershipsLoading: false,
        membershipsError: new Error('User must be logged in to view memberships'),
      };
    }

    // if the query qualifiers are passed in as null, then don't return anything. Not an error either.
    // It just means that the calling component wanted nothing fetched.
    if (personId === null) {
      return {
        memberships: [],
        isMembershipsLoading: false,
      };
    }

    const validOrgIds = includeAllStatuses ? getAllOrgIds() : getOrgIdsByStatus(status);
    if (orgId && profileKeys && !isGod && !validOrgIds.includes(orgId)) {
      return {
        memberships: [],
        isMembershipsLoading: false,
        membershipsError: new Error('User not authorized to get these memberships'),
      };
    }

    return {
      memberships: memberships || [],
      isMembershipsLoading: isMembershipsLoading || !profileKeys,
      membershipsError,
    };
  }, [
    memberships, 
    isMembershipsLoading, 
    membershipsError, 
    profileKeys,
    personId,
    orgId, 
    isGod, 
    getAllOrgIds,
    getOrgIdsByStatus, 
    status,
    includeAllStatuses
  ]);

  return memoizedReturn;
}
